/**
 * Template Workflow Dashboard - Unified Template & Workflow Management
 *
 * Combines template selection with workflow management in a single,
 * streamlined interface optimized for rapid workflow creation.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Group,
  Button,
  Modal,
  Paper,
  Tabs,
  Badge,
  Alert,
  ActionIcon,
  Tooltip,
  Title,
  Text,
  Grid,
  Card,
  ThemeIcon,
} from '@mantine/core';
import {
  IconPlus,
  IconTemplate,
  IconList,
  IconRocket,
  IconStar,
  IconClock,
  IconActivity,
  IconSparkles,
  IconEye,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';

import { StreamlinedTemplateViewer } from './StreamlinedTemplateViewer';
import { WorkflowTemplateGallery } from './WorkflowTemplateGallery';
import { useActiveWorkflows } from '@ferp/react-integration';
import { WorkflowTemplate } from '../templates/SystemWorkflowTemplates';
import { WorkflowEntity } from '@ferp/repositories';

// Import WorkflowCard from the correct path
interface WorkflowCardProps {
  workflow: WorkflowEntity;
  onEdit?: (workflow: WorkflowEntity) => void;
  onView?: (workflow: WorkflowEntity) => void;
  onRefresh?: () => void;
}

// Simple WorkflowCard component for the dashboard
const WorkflowCard: React.FC<WorkflowCardProps> = ({ workflow, onEdit, onView }) => (
  <Card shadow="sm" padding="lg" radius="md" withBorder>
    <Group justify="space-between" mb="xs">
      <Text fw={500}>{workflow.name}</Text>
      <Badge color="blue" variant="light">
        {workflow.status || 'Active'}
      </Badge>
    </Group>
    <Text size="sm" c="dimmed" mb="md">
      {workflow.description}
    </Text>
    <Group>
      <Button size="xs" variant="light" onClick={() => onView?.(workflow)}>
        View
      </Button>
      <Button size="xs" variant="outline" onClick={() => onEdit?.(workflow)}>
        Edit
      </Button>
    </Group>
  </Card>
);

export interface TemplateWorkflowDashboardProps {
  tenantId: string;
  userRoles: string[];
  onCreateWorkflow?: (template: WorkflowTemplate) => void;
  onEditWorkflow?: (workflow: WorkflowEntity) => void;
}

// Quick stats card
const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  description?: string;
}> = ({ title, value, icon, color, description }) => (
  <motion.div whileHover={{ scale: 1.02 }}>
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm" tt="uppercase" fw={700}>
            {title}
          </Text>
          <Text fw={700} size="xl" mt={4}>
            {value}
          </Text>
          {description && (
            <Text size="xs" c="dimmed" mt={4}>
              {description}
            </Text>
          )}
        </div>
        <ThemeIcon color={color} variant="light" size="xl">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  </motion.div>
);

export const TemplateWorkflowDashboard: React.FC<TemplateWorkflowDashboardProps> = ({
  tenantId,
  userRoles,
  onCreateWorkflow,
  onEditWorkflow,
}) => {
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateViewMode, setTemplateViewMode] = useState<'streamlined' | 'gallery'>('streamlined');
  const [activeTab, setActiveTab] = useState<string>('workflows');

  // Get active workflows
  const { data: activeWorkflows, loading: workflowsLoading } = useActiveWorkflows();

  // Handle template selection
  const handleTemplateSelect = (template: WorkflowTemplate) => {
    setShowTemplateModal(false);
    onCreateWorkflow?.(template);
  };

  // Quick action buttons
  const QuickActions = () => (
    <Group gap="md">
      <Button
        leftSection={<IconPlus size={16} />}
        onClick={() => setShowTemplateModal(true)}
        size="sm"
      >
        Create from Template
      </Button>
      <Tooltip label="Switch template view">
        <ActionIcon
          variant="light"
          onClick={() => setTemplateViewMode(mode =>
            mode === 'streamlined' ? 'gallery' : 'streamlined'
          )}
        >
          {templateViewMode === 'streamlined' ? <IconList size={16} /> : <IconSparkles size={16} />}
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  return (
    <Container size="xl" px="md">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" ta="right">
          <div>
            <Title order={1}>Workflow Center</Title>
            <Text c="dimmed" size="lg">
              Manage workflows and create new ones from templates
            </Text>
          </div>
          <QuickActions />
        </Group>

        {/* Quick stats */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Active Workflows"
              value={activeWorkflows?.length || 0}
              icon={<IconActivity size={20} />}
              color="blue"
              description="Currently running"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Templates Available"
              value={50} // This would come from template service
              icon={<IconTemplate size={20} />}
              color="green"
              description="Ready to use"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Recent Creations"
              value={12}
              icon={<IconClock size={20} />}
              color="orange"
              description="Last 7 days"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Success Rate"
              value={98}
              icon={<IconStar size={20} />}
              color="purple"
              description="Completion rate %"
            />
          </Grid.Col>
        </Grid>

        {/* Main content tabs */}
        <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value || 'workflows')}>
          <Tabs.List>
            <Tabs.Tab value="workflows" leftSection={<IconList size={16} />}>
              Active Workflows
            </Tabs.Tab>
            <Tabs.Tab value="templates" leftSection={<IconTemplate size={16} />}>
              Templates
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="workflows" pt="lg">
            <Stack gap="lg">
              {workflowsLoading ? (
                <Text>Loading workflows...</Text>
              ) : activeWorkflows && activeWorkflows.length > 0 ? (
                <Grid>
                  {activeWorkflows.map(workflow => (
                    <Grid.Col key={workflow.id} span={{ base: 12, md: 6, lg: 4 }}>
                      <WorkflowCard
                        workflow={workflow}
                        onEdit={onEditWorkflow}
                        onView={(wf: WorkflowEntity) => console.log('View workflow:', wf.id)}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              ) : (
                <Paper p="xl" withBorder radius="md">
                  <Stack ta="center" gap="md">
                    <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
                      <IconRocket size={32} />
                    </ThemeIcon>
                    <div style={{ textAlign: 'center' }}>
                      <Text fw={500} size="lg" mb="xs">No active workflows</Text>
                      <Text c="dimmed" mb="lg">
                        Get started by creating a workflow from one of our templates
                      </Text>
                      <Button
                        leftSection={<IconSparkles size={16} />}
                        onClick={() => setShowTemplateModal(true)}
                      >
                        Browse Templates
                      </Button>
                    </div>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="templates" pt="lg">
            <Stack gap="lg">
              <Group justify="between">
                <Text size="sm" c="dimmed">
                  {templateViewMode === 'streamlined'
                    ? 'Streamlined view - Quick template selection'
                    : 'Gallery view - Detailed template browser'
                  }
                </Text>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconEye size={14} />}
                  onClick={() => setTemplateViewMode(mode =>
                    mode === 'streamlined' ? 'gallery' : 'streamlined'
                  )}
                >
                  Switch View
                </Button>
              </Group>

              {templateViewMode === 'streamlined' ? (
                <StreamlinedTemplateViewer
                  onSelectTemplate={handleTemplateSelect}
                  tenantId={tenantId}
                  userRoles={userRoles}
                  previewMode={true}
                />
              ) : (
                <WorkflowTemplateGallery
                  onSelectTemplate={handleTemplateSelect}
                  tenantId={tenantId}
                  userRoles={userRoles}
                />
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {/* Template selection modal */}
        <Modal
          opened={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          title="Choose a Template"
          size="xl"
          padding="xl"
        >
          <StreamlinedTemplateViewer
            onSelectTemplate={handleTemplateSelect}
            tenantId={tenantId}
            userRoles={userRoles}
          />
        </Modal>
      </Stack>
    </Container>
  );
};

export default TemplateWorkflowDashboard;